import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config';
import { generateInsurancePdf } from '../services/insuranceService';
import BottomSheet from './BottomSheet';
import InsurancePdfBottomSheet from './InsurancePdfBottomSheet';
import '../styles/components/collectionModal.css';

interface CollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId?: number; // undefined for new collection
  initialName?: string; // Pass the name directly when editing
  onSave: (id: number | undefined, name: string, description: string, collectionType: 'generic' | 'checklist' | 'stock' | 'shopping' | 'marketplace' | 'grocery') => void;
  onShare?: (id: number, collectionType?: string) => void;
  onDelete?: (id: number, name: string) => void;
}

const CollectionModal: React.FC<CollectionModalProps> = ({
  isOpen,
  onClose,
  collectionId,
  initialName,
  onSave,
  onShare,
  onDelete,
}) => {
  const { token, user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Collection type: 'generic' | 'checklist' | 'stock' | 'shopping' | 'marketplace' | 'grocery'
  const [collectionType, setCollectionType] = useState<'generic' | 'checklist' | 'stock' | 'shopping' | 'marketplace' | 'grocery'>('generic');
  const [loading, setLoading] = useState(false);
  const [insuranceStatus, setInsuranceStatus] = useState<'idle' | 'in_progress' | 'success' | 'error'>('idle');
  const [insuranceMessage, setInsuranceMessage] = useState('');
  const [isInsuranceSheetOpen, setIsInsuranceSheetOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Initialize or fetch collection details
  useEffect(() => {
    if (isOpen && collectionId && token) {
      // Fetch specific collection details
      setLoading(true);
      if (initialName) {
        setName(initialName);
      }
      
      fetch(API_ENDPOINTS.GET_COLLECTION(collectionId), {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(data => {
          // Backend returns collection directly, not wrapped in {collection: ...}
          if (data && data.id) {
            if (!initialName) {
              setName(data.name);
            }
            setDescription(data.description || '');
            // Set collection type directly
            setCollectionType(data.collection_type || 'generic');
          }
          setLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch collection:', err);
          setLoading(false);
        });
    } else if (isOpen && !collectionId) {
      // Reset for new collection
      setName('');
      setDescription('');
      setCollectionType('generic');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, collectionId, initialName, token]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(collectionId, name.trim(), description.trim(), collectionType);
  };

  const handleShare = () => {
    if (collectionId && onShare) {
      onShare(collectionId, collectionType);
    }
  };

  const handleDelete = () => {
    if (collectionId && onDelete) {
      onDelete(collectionId, name);
    }
  };

  const handleInsurancePdfClick = () => {
    setIsInsuranceSheetOpen(true);
  };

  const handleInsurancePdfSend = async (userName: string, userEmail: string) => {
    if (!collectionId || !token) return;
    setIsGeneratingPdf(true);
    setInsuranceStatus('in_progress');
    setInsuranceMessage('Generating insurance document…');
    try {
      await generateInsurancePdf(token, collectionId, userName, userEmail);
      setInsuranceStatus('success');
      setInsuranceMessage('Insurance document is being sent to your email.');
      setIsInsuranceSheetOpen(false);
    } catch (error: any) {
      console.error('Failed to generate insurance PDF:', error);
      setInsuranceStatus('error');
      setInsuranceMessage(error?.message || 'Failed to generate insurance PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
      // Auto-dismiss toast after a short delay
      setTimeout(() => {
        setInsuranceStatus('idle');
        setInsuranceMessage('');
      }, 4000);
    }
  };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={onClose} title="Collection" className="collection-modal-sheet">
        {loading ? (
          <div className="collection-modal-loading">Loading...</div>
        ) : (
          <>
            {/* Form */}
            <div className="collection-modal-form">
              <div className="collection-modal-field">
                <label className="collection-modal-field-label">Name</label>
                <input
                  type="text"
                  className="collection-modal-input"
                  placeholder="Collection name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) handleSave();
                    if (e.key === 'Escape') onClose();
                  }}
                  enterKeyHint="done"
                />
              </div>

              <div className="collection-modal-field description-field">
                <label className="collection-modal-field-label">Description</label>
                <textarea
                  className="collection-modal-textarea"
                  placeholder="Description (optional)"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  enterKeyHint="done"
                />
              </div>

              <div className="collection-modal-field">
                <div className="collection-modal-type-options">
                <label className={`collection-modal-radio-label ${collectionType === 'generic' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="collectionType"
                    className="collection-modal-radio"
                    checked={collectionType === 'generic'}
                    onChange={() => setCollectionType('generic')}
                  />
                  <span>📁 Generic</span>
                </label>

                <label className={`collection-modal-radio-label ${collectionType === 'shopping' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="collectionType"
                    className="collection-modal-radio"
                    checked={collectionType === 'shopping'}
                    onChange={() => setCollectionType('shopping')}
                  />
                  <span>🛒 Shopping List</span>
                </label>

                <label className={`collection-modal-radio-label ${collectionType === 'checklist' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="collectionType"
                    className="collection-modal-radio"
                    checked={collectionType === 'checklist'}
                    onChange={() => setCollectionType('checklist')}
                  />
                  <span>✓ Checklist</span>
                </label>

                <label className={`collection-modal-radio-label ${collectionType === 'stock' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="collectionType"
                    className="collection-modal-radio"
                    checked={collectionType === 'stock'}
                    onChange={() => setCollectionType('stock')}
                  />
                  <span>📦 Stock Keeper</span>
                </label>

                <label className={`collection-modal-radio-label ${collectionType === 'marketplace' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="collectionType"
                    className="collection-modal-radio"
                    checked={collectionType === 'marketplace'}
                    onChange={() => setCollectionType('marketplace')}
                  />
                  <span>🏷️ Marketplace</span>
                </label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="collection-modal-actions-inline">
              <button className="collection-modal-btn save-btn" onClick={handleSave}>
                Save
              </button>
              {collectionId && onShare && (
                <button className="collection-modal-link share-link" onClick={handleShare}>
                  {collectionType === 'marketplace' ? 'Publish' : 'Share'}
                </button>
              )}
              {collectionId && (
                <button className="collection-modal-link insurance-link" onClick={handleInsurancePdfClick}>
                  Insurance PDF
                </button>
              )}
              {collectionId && onDelete && (
                <button className="collection-modal-link delete-link" onClick={handleDelete}>
                  Delete
                </button>
              )}
            </div>
          </>
        )}
      </BottomSheet>

      {insuranceStatus !== 'idle' && (
        <div className={`insurance-toast insurance-toast-${insuranceStatus}`}>
          <div className="insurance-toast-inner">
            <div className="insurance-toast-message">{insuranceMessage}</div>
            {insuranceStatus === 'in_progress' && (
              <div className="insurance-toast-progress">
                <div className="insurance-toast-progress-bar" />
              </div>
            )}
          </div>
        </div>
      )}

      <InsurancePdfBottomSheet
        isOpen={isInsuranceSheetOpen}
        onClose={() => setIsInsuranceSheetOpen(false)}
        onSend={handleInsurancePdfSend}
        userName={user?.display_name || ''}
        userEmail={user?.email || ''}
        isLoading={isGeneratingPdf}
      />
    </>
  );
};

export default CollectionModal;
